import { ModuleGroupingTypes, PageModule, PageModuleType } from '@/types/module.types';
import { EventHomepage } from '@/types/homepage.types';
import { Session } from '@/types/session.types';
import { Speaker } from '@/types/speakers.types';

/**
 * When sync_speakers is enabled, merges speakers from every enabled speaker
 * module in the event into the overview speaker module of each session so
 * the stored data reflects the complete aggregated list.
 *
 * The "overview" speaker module is any speaker module that is not marked
 * is_custom and is not in an Extras or custom module_grouping.
 */
export const aggregateSpeakersOnSync = (sessions: Session[], homepage: EventHomepage | null): Session[] => {
  const allModules: PageModule[] = [
    ...(homepage?.modules ?? []),
    ...(homepage?.post_register_home_modules ?? []),
    ...sessions.flatMap((s) => s.modules),
  ];

  const seenIds = new Set<number>();
  const allSpeakers: Speaker[] = [];
  for (const module of allModules) {
    if (module.type !== PageModuleType.speakers || !module.is_on) continue;
    for (const sp of (module.modules ?? []) as Speaker[]) {
      if (typeof sp?.speaker === 'number' && !seenIds.has(sp.speaker)) {
        seenIds.add(sp.speaker);
        allSpeakers.push(sp);
      }
    }
  }

  return sessions.map((s) => {
    const excludedModuleIds = new Set<number>(
      (s.module_grouping ?? [])
        .filter((g) => g.type === ModuleGroupingTypes.Extras || g.type === ModuleGroupingTypes.custom)
        .flatMap((g) => g.modules)
    );
    return {
      ...s,
      modules: s.modules.map((m) => {
        if (m.type !== PageModuleType.speakers || m.is_custom) return m;
        if (m.id !== undefined && excludedModuleIds.has(m.id)) return m;
        const existingIds = new Set<number>(((m.modules ?? []) as Speaker[]).map((sp) => sp.speaker));
        const toAdd = allSpeakers.filter((sp) => !existingIds.has(sp.speaker));
        if (!toAdd.length) return m;
        return {
          ...m,
          modules: [...(m.modules ?? []), ...toAdd],
          content_modules: [...(m.content_modules ?? []), ...toAdd.map((sp) => sp.speaker)],
        };
      }),
    };
  });
};
